import type { CSSProperties } from 'react'
import type { BoardAged, BoardCell } from '../api/easeedeskBoards'

/**
 * Stilurile tabloului de tichete, luate din macheta veche
 * (`views/version-3/dashboards/easeedesk/tickets_dashboard/index.php`) si din
 * `public/js/dashboards/easeedesk.js`, care desena casutele cu `style=` in
 * sir. Nu exista foaie de stil pentru ecranul asta - totul era in linie - deci
 * "aceleasi foi" inseamna aici "aceleasi masuri", scrise o data si aplicate
 * la fel pe fiecare casuta.
 *
 * Randul "Late Resolved" e mai inalt decat celelalte (65 in loc de 50, cu
 * randul de scris 50 in loc de 40); asa era si in JS.
 */

export const WALL_BACKGROUND = '#E9EDEF'

/** Capul de coloana. Primul e cu o jumatate de procent mai lat, ca in macheta. */
export function centreHeadStyle(first: boolean): CSSProperties {
  return {
    width: first ? '10.5%' : '10%',
    float: 'left',
    minHeight: '1px',
    paddingRight: '1px',
    paddingLeft: '1px',
    textAlign: 'center',
    marginTop: '5px',
  }
}

export function centreHeadInnerStyle(color: string): CSSProperties {
  return {
    backgroundColor: color,
    color: '#F2F2F2',
    fontSize: '25px',
    padding: '2px',
    height: '70px',
    overflow: 'hidden',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    textOverflow: 'ellipsis',
  }
}

/** Eticheta din stanga randului, pe culoarea randului. */
export function rowLabelStyle(color: string, tall: boolean): CSSProperties {
  return {
    float: 'left',
    width: '9%',
    textAlign: 'center',
    fontSize: '25px',
    height: tall ? '65px' : '50px',
    backgroundColor: color,
    color: '#F2F2F2',
    padding: '5px',
    marginTop: '5px',
    marginLeft: '-8px',
    marginRight: '10px',
    ...(tall ? { lineHeight: '30px' } : {}),
  }
}

/** Eticheta goala de dinaintea randurilor de medii si de activitate. */
export const EMPTY_LABEL_STYLE: CSSProperties = {
  float: 'left',
  width: '9%',
  textAlign: 'center',
  fontSize: '25px',
  height: '50px',
  color: '#F2F2F2',
  padding: '15px',
  marginTop: '5px',
  marginLeft: '-8px',
  marginRight: '10px',
}

export const VALUE_BOX_STYLE: CSSProperties = {
  width: '5%',
  float: 'left',
  minHeight: '1px',
  paddingLeft: '1px',
  textAlign: 'center',
  marginTop: '5px',
}

export const AGE_BOX_STYLE: CSSProperties = {
  width: '5%',
  float: 'left',
  minHeight: '1px',
  paddingRight: '1px',
  textAlign: 'center',
  marginTop: '5px',
}

export function valueTileStyle(color: string, tall: boolean): CSSProperties {
  return {
    borderLeft: `10px solid ${color}`,
    backgroundColor: 'white',
    color: 'black',
    padding: '5px',
    overflow: 'hidden',
    fontSize: '25px',
    height: tall ? '65px' : '50px',
    fontWeight: 'bold',
    lineHeight: tall ? '50px' : '40px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }
}

export function ageTileStyle(tall: boolean): CSSProperties {
  return {
    backgroundColor: 'white',
    color: 'black',
    padding: '5px',
    overflow: 'hidden',
    fontSize: '12px',
    height: tall ? '65px' : '50px',
    fontWeight: 'bold',
    lineHeight: tall ? '50px' : '40px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }
}

/** Casutele de jos: doua sau trei etaje, pe albastru sau pe gri. */
export function summaryBoxStyle(last: boolean): CSSProperties {
  return {
    width: last ? '12.2%' : '13%',
    float: 'left',
    minHeight: '1px',
    paddingRight: '5px',
    paddingLeft: '5px',
    textAlign: 'center',
    marginTop: '5px',
  }
}

export function summaryValueStyle(color: string): CSSProperties {
  return {
    width: '100%',
    backgroundColor: color,
    color: '#F2F2F2',
    height: '40px',
    lineHeight: '40px',
    fontSize: '35px',
    fontWeight: 'bold',
  }
}

export function summaryLabelStyle(color: string, height: string, padding?: string): CSSProperties {
  return {
    width: '100%',
    backgroundColor: color,
    color: '#F2F2F2',
    height,
    lineHeight: '20px',
    fontSize: '20px',
    cursor: 'default',
    fontWeight: 'bold',
    ...(padding ? { padding } : {}),
  }
}

/** Ce scrie in casuta a doua: 0 tichete inseamna fara legatura, ca in JS. */
export function hasTicket(cell: BoardCell | BoardAged | undefined): boolean {
  return !!cell && cell.ticket > 0
}

/** Ziua de azi, in forma pe care o trimitea ecranul vechi. */
export function todayRange(now: Date = new Date()): { from: string; to: string } {
  return rangeOf('Today', now)
}

/**
 * Cele sase perioade din `daterangepicker` al machetei vechi, cu numele lor.
 *
 * Ecranul vechi are in cap o casuta `.reportrange` cu un calendar si aceste
 * sase scurtaturi; ele umplu `filterFrom` / `filterTo`, si de acolo pleaca
 * cererea. In v4 casuta lipsea cu totul: tabloul arata mereu ziua curenta,
 * fiindca perioada era o valoare fara buton
 * (`const [range] = useState(todayRange)`), desi capatul stie de mult sa
 * primeasca `?from=&to=`.
 *
 * Calendarul cu zile alese de mana nu se aduce: ar cere biblioteca
 * `daterangepicker`, care nu e in v4. Scurtaturile sunt cele care se apasa,
 * si sunt aceleasi cuvinte.
 */
export const BOARD_RANGES = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Last Month'] as const

export type BoardRangeName = (typeof BOARD_RANGES)[number]

function dayEdges(from: Date, to: Date): { from: string; to: string } {
  const stamp = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  return { from: `${stamp(from)} 00:00`, to: `${stamp(to)} 23:59` }
}

/** Perioada unei scurtaturi, in forma pe care o trimitea ecranul vechi. */
export function rangeOf(name: BoardRangeName, now: Date = new Date()): { from: string; to: string } {
  const day = (offset: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)

  switch (name) {
    case 'Yesterday':
      return dayEdges(day(-1), day(-1))
    case 'Last 7 Days':
      return dayEdges(day(-6), day(0))
    case 'Last 30 Days':
      return dayEdges(day(-29), day(0))
    case 'This Month':
      return dayEdges(new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0))
    case 'Last Month':
      return dayEdges(new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0))
    default:
      return dayEdges(day(0), day(0))
  }
}

/** Ceasul din coltul stang: "9:05", ca `tick()` din easeedesk.js. */
export function clockText(now: Date = new Date()): string {
  const minutes = now.getMinutes()
  return `${now.getHours()}:${minutes < 10 ? '0' + minutes : minutes} `
}
